import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QrCode, Footprints, BellRing, Store, Ticket, ShieldCheck } from "lucide-react";
import { SiteHeader, SiteFooter } from "@/components/Layout";
import { LogoStacked, TaglineText } from "@/components/Brand";
import { pub, money } from "@/lib/api";

const HERO_IMG = "/brand/hero.jpg";

const steps = [
  { icon: QrCode, title: "Scan & order", text: "Scan the venue's LineGone QR code. The menu opens in your phone browser — no app, no account." },
  { icon: Footprints, title: "Do other things", text: "Pay by card, Apple Pay or Google Pay, then get on with your day. Your spot in line is held for you." },
  { icon: BellRing, title: "Get pinged", text: "Your status page turns amber and chimes the moment staff mark your order Ready." },
];

const Step = ({ s, i }) => (
  <div className={`card-lg rise rise-${i + 2} p-6`} data-testid={`how-step-${i}`}>
    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand text-white"><s.icon size={22} /></div>
    <h3 className="text-xl font-extrabold">{i + 1}. {s.title}</h3>
    <p className="mt-2 text-sm leading-relaxed text-muted2">{s.text}</p>
  </div>
);

const Hero = () => (
  <section className="mx-auto grid max-w-6xl items-center gap-10 px-4 pb-12 pt-10 sm:px-6 lg:grid-cols-[1.1fr_1fr] lg:pt-16">
    <div>
      <LogoStacked className="rise w-48 sm:w-60" />
      <h1 className="rise rise-2 mt-8 text-4xl font-black leading-[1.05] text-ink sm:text-5xl lg:text-6xl" data-testid="landing-tagline"><TaglineText /></h1>
      <p className="rise rise-3 mt-5 max-w-lg text-base text-muted2 sm:text-lg">
        Order, pay and check in at cafés, food trucks and events without queuing or installing an app.
      </p>
      <div className="rise rise-4 mt-8 flex flex-wrap gap-3">
        <Link to="/v/maple-bean-cafe" className="btn-primary" data-testid="cta-demo-cafe">Try Maple Bean Café</Link>
        <Link to="/v/northern-lights-meetup" className="btn-secondary" data-testid="cta-demo-event">Northern Lights Meetup</Link>
      </div>
      <p className="mt-3 text-xs text-muted2">Both are demo venues running in Stripe test mode.</p>
    </div>
    <div className="relative rise rise-3">
      <img src={HERO_IMG} alt="Lattes on a café counter" className="aspect-[4/5] w-full rounded-[2rem] object-cover sm:aspect-[4/3] lg:aspect-[4/5]" />
      <div className="absolute -bottom-5 left-5 right-5 rounded-2xl bg-ready p-4 text-ink shadow-xl sm:left-auto sm:w-72" data-testid="hero-ready-card">
        <div className="eyebrow !text-ink">Order A07 · Maple Bean Café</div>
        <div className="font-heading text-2xl font-black">Ready! Come grab it.</div>
      </div>
    </div>
  </section>
);

const ForVenues = () => {
  const [fees, setFees] = useState(null);
  useEffect(() => { pub.get("/fees").then((r) => setFees(r.data)).catch(() => {}); }, []);
  const pct = fees ? `${fees.commission_pct}%` : "…";
  const pass = fees ? money(fees.pass_fee_cents) : "…";
  return (
  <section className="bg-brand-pale py-16" data-testid="for-venues-section">
    <div className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 lg:grid-cols-2">
      <div>
        <div className="eyebrow">For venues</div>
        <h2 className="mt-2 text-3xl font-black sm:text-4xl">Shorter lines. Paid before they reach you.</h2>
        <p className="mt-4 text-muted2">Connect your own Stripe account, print your QR sign and start taking orders and event passes. Money goes straight to your Stripe account.</p>
        <Link to="/dashboard" className="btn-primary mt-6" data-testid="cta-list-venue"><Store size={18} /> List your venue</Link>
      </div>
      <ul className="grid gap-3 text-sm">
        {[[Store, `${pct} of the pre-tax item subtotal per order — no monthly fees`], [Ticket, `${pass} per paid event pass · free passes are free`],
          [ShieldCheck, "GST applied and reported for you — exportable CSV for your return"], [BellRing, "Staff board with sound alerts, door scanner and activity log"]].map(([I, t]) => (
          <li key={t} className="flex items-start gap-3 rounded-2xl bg-white p-4"><I size={18} className="mt-0.5 shrink-0 text-brand" /> {t}</li>
        ))}
      </ul>
    </div>
  </section>
  );
};

export default function Landing() {
  return (
    <div data-testid="landing-page">
      <SiteHeader right={<><Link to="/dashboard" className="link-lg whitespace-nowrap text-sm" data-testid="header-venues-link">For venues</Link>
        <Link to="/v/maple-bean-cafe" className="btn-primary whitespace-nowrap !px-3 !py-2 text-sm sm:!px-4" data-testid="header-demo-btn">Try demo</Link></>} />
      <Hero />
      <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
        <div className="eyebrow">How LineGone works</div>
        <div className="mt-5 grid gap-4 md:grid-cols-3">{steps.map((s, i) => <Step key={s.title} s={s} i={i} />)}</div>
      </section>
      <ForVenues />
      <SiteFooter />
    </div>
  );
}
